import type { ErrorEvent, NodeOptions } from '@sentry/nextjs';

const REDACTED = '[redacted]';

// @sentry/nextjs does not re-export the DataCollection type itself.
type DataCollection = NonNullable<NodeOptions['dataCollection']>;

/**
 * What the Sentry SDK may collect, shared by the browser, Node and edge inits.
 *
 * SDK v11 replaced `sendDefaultPii: false` with this option, and its defaults
 * collect almost everything: cookies, headers, bodies, query params, local
 * variables and the user's IP (gated by `userInfo`). Every category that can
 * carry a session cookie, a JWT, a pairing key or a broker balance is off.
 * Source context lines stay on: they are our code, not user data.
 */
export const SENTRY_DATA_COLLECTION: DataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: { request: false, response: false },
  httpBodies: [],
  urlQueryParams: false,
  stackFrameVariables: false,
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
};

/**
 * Query keys whose value is a live credential.
 *
 * `token` backs the password-reset and email-verification links, so a browser
 * error raised on either page would otherwise ship a working account-recovery
 * token to Sentry. `access_token` and `license` mirror the backend scrubber so
 * both halves of the stack redact the same names.
 */
const SECRET_QUERY_KEYS = ['token', 'access_token', 'license'];

const SECRET_PARAM_RE = new RegExp(
  `([?&])(${SECRET_QUERY_KEYS.join('|')})=[^&#]*`,
  'gi'
);

/** Replaces the value of every credential-bearing query param in a URL. */
export function scrubUrl<T>(url: T): T {
  if (typeof url !== 'string' || url.length === 0) return url;

  return url.replace(
    SECRET_PARAM_RE,
    (_match, separator: string, key: string) => `${separator}${key}=${REDACTED}`
  ) as T;
}

/**
 * Redacts credentials from an event before it leaves the browser or server.
 *
 * Sentry records the page URL on the event itself and again on every navigation
 * and fetch breadcrumb, so all three have to be cleaned; scrubbing only
 * `request.url` leaves the same token sitting in the breadcrumb trail.
 *
 * Never drops the event: losing the error is worse than reporting it with
 * fields blanked.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb.data;
    if (!data) continue;

    for (const field of ['url', 'to', 'from'] as const) {
      if (typeof data[field] === 'string') {
        data[field] = scrubUrl(data[field]);
      }
    }
  }

  return event;
}
